import React from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// The 50 jurisdictions the Regulens database supports (48 states + DC).
// Kept in sync with the backend's state_tax_nexus_rule + state_privacy_law tables.
// 2026-09 expansion added: CT, DE, MA, MD, MT, NH, NJ, OH, OR, PA, UT.
// Alias `ALL_STATES_39` retained for backward-compat with existing imports.
export const ALL_STATES = [
  "AK", "AL", "AR", "AZ", "CA", "CO", "CT", "DC", "DE", "FL",
  "GA", "HI", "IA", "ID", "IL", "IN", "KS", "KY", "LA", "MA",
  "MD", "ME", "MI", "MN", "MO", "MS", "MT", "NC", "ND", "NE",
  "NH", "NJ", "NM", "NV", "NY", "OH", "OK", "OR", "PA", "SC",
  "SD", "TN", "TX", "UT", "VA", "VT", "WA", "WI", "WV", "WY",
];
export const ALL_STATES_39 = ALL_STATES;   // legacy alias — do not use in new code

/**
 * "Manage My Jurisdictions" modal. Parent owns the selection state so it can
 * react to a save (invalidate cached audit + reload). This component is pure
 * presentation + interaction routing.
 */
export default function ManageJurisdictionsModal({
  open,
  onOpenChange,
  selected,
  onToggle,
  onSave,
  onSelectAll,
  onDeselectAll,
  saving,
}) {
  const empty = selected.length === 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] max-w-2xl"
        data-testid="manage-juris-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-heading">Manage My Jurisdictions</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-[var(--rg-text)] mb-2">
          Update which U.S. states your store operates in. Saving will invalidate
          your cached audit and prompt a fresh readiness check.
        </p>
        <div className="flex items-center gap-3 mb-2 text-xs">
          <button
            type="button"
            onClick={onSelectAll}
            className="text-[var(--rg-sage)] hover:underline font-semibold uppercase tracking-[0.14em]"
            data-testid="juris-select-all"
          >
            Select All
          </button>
          <span className="text-[var(--rg-muted)]">·</span>
          <button
            type="button"
            onClick={onDeselectAll}
            className="text-[var(--rg-red)] hover:underline font-semibold uppercase tracking-[0.14em]"
            data-testid="juris-deselect-all"
          >
            Deselect All
          </button>
        </div>
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-72 overflow-y-auto p-2 rounded border border-[var(--rg-border)] bg-[var(--rg-surface)]">
          {ALL_STATES.map((s) => {
            const on = selected.includes(s);
            return (
              <button
                type="button"
                key={s}
                onClick={() => onToggle(s)}
                className={`text-xs font-mono font-bold rounded-sm py-1.5 transition-fast border ${
                  on
                    ? "bg-[var(--rg-sage)] text-[var(--rg-on-sage)] border-[var(--rg-sage)]"
                    : "bg-[var(--rg-bg)] text-[var(--rg-text)] border-[var(--rg-border)] hover:border-[var(--rg-border-hi)]"
                }`}
                data-testid={`juris-pill-${s}`}
              >
                {s}
              </button>
            );
          })}
        </div>
        <div className="text-xs text-[var(--rg-muted)] mt-1" data-testid="juris-selected-count">
          {selected.length} selected
        </div>
        {empty && (
          <p className="text-xs text-[var(--rg-red)] mt-1" data-testid="juris-zero-error">
            At least one jurisdiction must be selected.
          </p>
        )}
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]"
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={saving || empty}
            className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold disabled:opacity-40"
            data-testid="save-jurisdictions"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                Saving…
              </>
            ) : (
              "Save & Prompt Fresh Audit"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
